<template>
  <div class="d-flex fill-height app-bg overflow-hidden position-relative font-sans" style="width: 100%; height: 100vh">
    <v-navigation-drawer
      v-model="drawer"
      absolute
      temporary
      color="#000000"
      dark
      width="280"
      v-if="$vuetify.breakpoint.smAndDown"
      style="z-index: 100">
      <div class="pa-6 font-weight-black white--text text-h5 tracking-tight">Roody<span class="primary--text">.</span></div>
      <v-list dense nav dark class="mt-2 px-4">
        <v-list-item-group v-model="selectedFolder" active-class="active-folder-mobile">
          <v-list-item
            v-for="folder in folders"
            :key="folder.name"
            :value="folder.name"
            class="rounded-lg mb-2"
            @click="
              loadMails(folder.name)
              drawer = false
            "
            @dragover.prevent
            @drop="onDrop($event, folder.name)">
            <v-list-item-icon
              ><v-icon size="20">{{ getFolderIcon(folder.name) }}</v-icon></v-list-item-icon
            >
            <v-list-item-content
              ><v-list-item-title class="font-weight-medium">{{ formatFolderName(folder.name) }}</v-list-item-title></v-list-item-content
            >
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>

    <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex flex-column sidebar-panel pt-6">
      <div class="px-6 mb-8 d-flex align-center">
        <div class="brand-glow mr-3 d-flex align-center justify-center rounded-lg">
          <span class="font-weight-black white--text">R</span>
        </div>
        <span class="font-weight-bold white--text text-h6 tracking-tight" style="letter-spacing: -0.5px">Roody Mail</span>
      </div>

      <div class="px-4 mb-6">
        <v-btn
          block
          depressed
          large
          class="gradient-btn rounded-xl text-capitalize white--text font-weight-bold shadow-lg"
          @click="triggerSync"
          :loading="isSyncingActive">
          <v-icon left size="20">mdi-refresh</v-icon> Sync
        </v-btn>
      </div>

      <div class="px-3 flex-grow-1">
        <div class="overline grey--text text--darken-2 font-weight-bold px-3 mb-2">FOLDERS</div>
        <v-list dense nav color="transparent" dark class="pa-0">
          <v-list-item-group v-model="selectedFolder" active-class="active-folder-desktop">
            <v-list-item
              v-for="folder in folders"
              :key="folder.name"
              :value="folder.name"
              class="rounded-lg mb-1 nav-item-transition"
              @click="loadMails(folder.name)"
              @dragover.prevent
              @drop="onDrop($event, folder.name)">
              <v-list-item-icon class="mr-3">
                <v-icon small :color="selectedFolder === folder.name ? 'white' : 'grey'">{{ getFolderIcon(folder.name) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium body-2" :class="selectedFolder === folder.name ? 'white--text' : 'grey--text'">
                  {{ formatFolderName(folder.name) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </div>

      <div class="pa-4 mt-auto">
        <div class="d-flex align-center pa-3 rounded-xl glass-panel-dark">
          <v-avatar size="32" color="grey darken-3"><v-icon small>mdi-account</v-icon></v-avatar>
          <div class="ml-3 caption grey--text font-weight-bold">Admin User</div>
        </div>
      </div>
    </div>

    <div class="d-flex flex-column list-panel border-r-subtle" :class="{ 'd-none': $vuetify.breakpoint.smAndDown && showMobileDetail }">
      <div class="glass-header px-5 py-4 d-flex align-center flex-shrink-0 z-index-10">
        <v-btn icon small dark class="mr-3 hidden-md-and-up" @click="drawer = !drawer">
          <v-icon>mdi-menu</v-icon>
        </v-btn>
        <div class="font-weight-bold white--text text-h6 tracking-tight">Posteingang</div>
        <v-spacer></v-spacer>
        <v-btn icon small color="grey darken-1"><v-icon>mdi-filter-variant</v-icon></v-btn>
      </div>

      <div class="px-4 pb-2 pt-2 flex-shrink-0">
        <v-text-field
          v-model="search"
          placeholder="Suchen..."
          prepend-inner-icon="mdi-magnify"
          filled
          rounded
          dense
          hide-details
          dark
          color="primary"
          background-color="#1A1A1A"
          class="modern-search-field"></v-text-field>
      </div>

      <div class="flex-grow-1 overflow-y-auto custom-scrollbar px-3 pb-2 pt-2">
        <div v-if="loadingMails" class="mt-2 px-1">
          <v-skeleton-loader dark type="list-item-avatar-two-line" v-for="n in 6" :key="n" class="mb-3 transparent"></v-skeleton-loader>
        </div>

        <div v-else-if="filteredMails.length === 0" class="d-flex flex-column align-center justify-center fill-height grey--text text--darken-2">
          <v-icon color="grey darken-3" size="64" class="mb-4">mdi-inbox-outline</v-icon>
          <div class="font-weight-medium">Alles erledigt</div>
        </div>

        <div v-else class="d-flex flex-column">
          <div
            v-for="mail in filteredMails"
            :key="mail._id"
            @click="selectMail(mail)"
            draggable="true"
            @dragstart="onDragStart($event, mail)"
            class="mail-card rounded-xl mb-2 pa-4 pointer position-relative overflow-hidden"
            :class="{
              'active-mail-card': activeMail && activeMail._id === mail._id,
              'unread-mail': !mail.isRead,
              'is-processing': mail.category === 'Processing...',
            }">
            <div class="d-flex align-start justify-space-between mb-1">
              <div class="d-flex align-center" style="max-width: 80%">
                <v-avatar size="24" :color="getCategoryColor(mail.category)" class="mr-2 elevation-2">
                  <span class="white--text caption font-weight-bold" style="font-size: 10px !important">
                    {{ (mail.from.name || mail.from.address)[0].toUpperCase() }}
                  </span>
                </v-avatar>
                <span class="font-weight-bold text-truncate white--text body-2">
                  {{ mail.from.name || mail.from.address }}
                </span>
              </div>
              <span class="caption grey--text text--darken-1 font-weight-medium">{{ formatDate(mail.date) }}</span>
            </div>

            <div class="body-2 grey--text text--lighten-1 text-truncate mb-2 pl-1" :class="{ 'white--text font-weight-bold': !mail.isRead }">
              {{ mail.subject || "(Kein Betreff)" }}
            </div>

            <div class="d-flex align-center pl-1">
              <template v-if="mail.category === 'Processing...'">
                <div class="d-flex align-center">
                  <v-progress-circular indeterminate size="12" width="2" color="purple accent-2" class="mr-2"></v-progress-circular>
                  <span class="caption purple--text text--accent-2 font-weight-bold fade-in-out">AI analysiert...</span>
                </div>
              </template>
              <template v-else>
                <span class="caption grey--text text--darken-1 text-truncate" style="opacity: 0.8">
                  {{ mail.aiAnalysis ? mail.aiAnalysis.summary : "Keine Vorschau" }}
                </span>
              </template>
            </div>

            <div class="card-glow"></div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="d-flex flex-column detail-panel black"
      :class="{
        'mobile-fullscreen': $vuetify.breakpoint.smAndDown && showMobileDetail,
        'd-none': $vuetify.breakpoint.smAndDown && !showMobileDetail,
        'flex-grow-1': $vuetify.breakpoint.mdAndUp,
      }">
      <div v-if="!activeMail" class="d-flex align-center justify-center fill-height flex-column bg-pattern">
        <div class="d-flex align-center justify-center rounded-circle glass-panel mb-6 elevation-10" style="width: 120px; height: 120px">
          <v-icon size="56" color="grey darken-1">mdi-email-outline</v-icon>
        </div>
        <div class="grey--text text--darken-1 font-weight-medium">Wähle eine Nachricht aus</div>
      </div>

      <template v-else>
        <div class="glass-header px-6 py-3 d-flex align-center flex-shrink-0 z-index-10 border-b-subtle">
          <v-btn icon small @click="showMobileDetail = false" class="mr-2 hidden-md-and-up white--text">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <div class="d-flex align-center glass-pill pa-1">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="archiveMail(activeMail)" color="grey lighten-1"
                  ><v-icon size="20">mdi-archive-outline</v-icon></v-btn
                >
              </template>
              <span>Archivieren</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="deleteMail(activeMail)" color="grey lighten-1" class="mx-1"
                  ><v-icon size="20">mdi-delete-outline</v-icon></v-btn
                >
              </template>
              <span>Löschen</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openForward" color="grey lighten-1"
                  ><v-icon size="20">mdi-share-variant</v-icon></v-btn
                >
              </template>
              <span>Weiterleiten</span>
            </v-tooltip>
          </div>

          <div class="mx-3" style="height: 24px; width: 1px; background: rgba(255, 255, 255, 0.1)"></div>

          <v-btn
            text
            small
            color="purple accent-2"
            @click="showMagicDialog = true"
            class="rounded-lg px-2"
            style="background: rgba(224, 64, 251, 0.1)">
            <v-icon left small>mdi-auto-fix</v-icon> <span class="hidden-xs-only font-weight-bold">AI Ops</span>
          </v-btn>

          <v-btn
            v-if="activeMail.customerData && activeMail.customerData.mapped"
            color="success"
            small
            text
            class="ml-2 rounded-lg px-2"
            style="background: rgba(76, 175, 80, 0.1)"
            @click="showCustomerInfo = true">
            <v-icon left small>mdi-card-account-details</v-icon> <span class="hidden-xs-only font-weight-bold">Member</span>
          </v-btn>
          <v-chip v-else x-small outlined color="grey darken-1" class="ml-2">Guest</v-chip>
        </div>

        <div class="flex-grow-1 overflow-y-auto custom-scrollbar relative-wrapper bg-gradient-subtle">
          <div class="pa-6 pb-2">
            <h1 class="white--text text-h5 font-weight-bold mb-6 tracking-tight">{{ activeMail.subject }}</h1>

            <div class="d-flex align-center justify-space-between mb-6">
              <div class="d-flex align-center">
                <v-avatar color="grey darken-3" size="48" class="mr-4 elevation-4">
                  <span class="white--text font-weight-bold text-h6">{{ (activeMail.from.name || activeMail.from.address)[0].toUpperCase() }}</span>
                </v-avatar>
                <div>
                  <div class="white--text font-weight-bold subtitle-1">{{ activeMail.from.name }}</div>
                  <div class="grey--text text--lighten-1 caption">{{ activeMail.from.address }}</div>
                  <div class="grey--text text--darken-1 caption mt-1">An: {{ activeMail.to.map((t) => t.name || t.address).join(", ") }}</div>
                </div>
              </div>
              <div class="text-right">
                <div class="grey--text text--lighten-1 caption font-weight-bold">
                  {{ new Date(activeMail.date).toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short" }) }}
                </div>
                <div class="grey--text text--darken-1 caption">
                  {{ new Date(activeMail.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }}
                </div>
              </div>
            </div>

            <div v-if="activeMail.aiAnalysis" class="pa-4 rounded-xl mb-4 ai-analysis-box">
              <div class="d-flex align-center mb-2">
                <v-icon small color="purple accent-2" class="mr-2">mdi-shimmer</v-icon>
                <span class="caption purple--text text--accent-2 font-weight-bold tracking-wide">ROODY INSIGHTS</span>
              </div>
              <div class="grey--text text--lighten-2 body-2" style="line-height: 1.6">{{ activeMail.aiAnalysis.summary }}</div>
            </div>
          </div>

          <v-divider class="grey darken-4 mx-6"></v-divider>

          <div class="pa-6">
            <div class="mail-renderer pa-6 rounded-lg elevation-2 strip-images">
              <div v-html="activeMail.body"></div>
            </div>
          </div>

          <div class="pa-6 pt-2">
            <div v-if="isRegenerating || activeMail.category === 'Processing...'" class="text-center py-8">
              <div class="ai-loader mx-auto mb-4"></div>
              <div class="caption grey--text fade-in-out">Roody formuliert eine Antwort...</div>
            </div>

            <div v-else-if="activeMail.draft && !replyMode && !isForwarding" class="mt-4">
              <div class="ai-draft-card rounded-xl overflow-hidden elevation-6">
                <div class="d-flex align-center px-5 py-3 draft-header">
                  <v-icon small color="white" class="mr-2">mdi-robot-outline</v-icon>
                  <span class="subtitle-2 white--text font-weight-bold">Vorschlag</span>
                  <v-spacer></v-spacer>
                  <v-btn x-small text color="white" style="opacity: 0.7" @click="showMagicDialog = true">
                    <v-icon left x-small>mdi-refresh</v-icon> Neu generieren
                  </v-btn>
                </div>
                <div
                  class="pa-5 grey--text text--lighten-1 body-1 font-mono"
                  style="white-space: pre-wrap; line-height: 1.6"
                  v-html="activeMail.draft"></div>
                <div class="pa-3 grey darken-4 d-flex justify-end border-t-subtle">
                  <v-btn color="white" light depressed small class="font-weight-bold rounded-lg px-4" @click="initReply">
                    Verwenden <v-icon right small>mdi-arrow-down</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <div style="height: 140px"></div>
        </div>

        <div class="floating-action-bar pa-4">
          <v-expand-transition>
            <div v-if="replyMode" class="d-flex flex-column glass-panel-dark rounded-xl pa-4 elevation-12 border-glow">
              <div v-if="isForwarding" class="mb-3 px-1 d-flex align-center border-b-subtle pb-2">
                <span class="caption grey--text mr-3">An:</span>
                <input v-model="forwardTo" placeholder="Empfänger E-Mail..." class="modern-input flex-grow-1 font-weight-bold" />
              </div>
              <v-textarea
                v-model="replyText"
                outlined
                dense
                hide-details
                dark
                color="primary"
                background-color="transparent"
                auto-grow
                rows="4"
                class="no-border-input"
                placeholder="Schreibe deine Antwort..."></v-textarea>
              <div class="d-flex justify-end align-center mt-2 pt-2 border-t-subtle">
                <v-btn text small color="grey" class="mr-2 white--text rounded-lg" @click="replyMode = false">Abbrechen</v-btn>
                <v-btn color="primary" depressed class="rounded-lg gradient-btn shadow-md" @click="sendReply" :loading="sending">
                  <v-icon left small>mdi-send</v-icon> Senden</v-btn
                >
              </div>
            </div>

            <div v-else class="d-flex justify-center">
              <div class="glass-pill elevation-10 d-flex pa-1">
                <v-btn large text color="white" class="rounded-pill px-6" @click="initReply" :disabled="activeMail.category === 'Processing...'">
                  <v-icon left color="primary">mdi-reply</v-icon> Antworten
                </v-btn>
                <div class="my-2" style="width: 1px; background: rgba(255, 255, 255, 0.1)"></div>
                <v-btn
                  icon
                  large
                  color="grey lighten-1"
                  class="rounded-circle ml-1"
                  @click="openForward"
                  :disabled="activeMail.category === 'Processing...'">
                  <v-icon>mdi-share-variant</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </template>
    </div>

    <v-dialog v-model="showCustomerInfo" max-width="900" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="tacMember" color="#121212" dark class="rounded-xl border-glow">
        <div class="d-flex align-end pa-6 gradient-header">
          <v-avatar size="72" color="white" class="elevation-4 mr-5">
            <span class="primary--text text-h4 font-weight-black">{{ tacMember.initials }}</span>
          </v-avatar>
          <div>
            <div class="text-h4 font-weight-bold white--text mb-1">{{ tacMember.fullName }}</div>
            <v-chip label x-small color="rgba(255,255,255,0.1)" class="font-weight-bold letter-spacing-1">ID: {{ tacMember.memberNumber }}</v-chip>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon large class="mb-2" @click="showCustomerInfo = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="pa-0">
          <v-tabs v-model="memberTab" background-color="#121212" dark grow height="60" slider-color="primary">
            <v-tab class="font-weight-bold text-capitalize">Übersicht</v-tab>
            <v-tab class="font-weight-bold text-capitalize">Verträge ({{ tacMember.contracts.length }})</v-tab>
            <v-tab class="font-weight-bold text-capitalize">Notizen</v-tab>
          </v-tabs>

          <v-tabs-items v-model="memberTab" class="transparent pa-6" style="min-height: 400px; background: #000">
            <v-tab-item>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card flat color="#161616" class="pa-5 rounded-xl fill-height border-subtle">
                    <div class="overline grey--text mb-4 font-weight-bold">FINANZEN</div>
                    <div class="d-flex align-center justify-space-between mb-4">
                      <span class="grey--text text--lighten-1">Saldo Gesamt</span>
                      <span
                        class="text-h4 font-weight-black"
                        :class="parseFloat(tacMember.balanceTotal) < 0 ? 'red--text text--accent-2' : 'green--text text--accent-3'">
                        CHF {{ tacMember.balanceTotal }}
                      </span>
                    </div>
                    <v-divider class="grey darken-4 mb-4"></v-divider>
                    <div class="d-flex justify-space-between mb-2">
                      <span class="grey--text">Abo-Konto</span>
                      <span class="white--text font-weight-bold">CHF {{ tacMember.balanceMembership }}</span>
                    </div>
                    <div class="d-flex justify-space-between">
                      <span class="grey--text">Konsum-Konto</span>
                      <span class="white--text font-weight-bold">CHF {{ tacMember.balanceConsumption }}</span>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card flat color="#161616" class="pa-5 rounded-xl fill-height border-subtle">
                    <div class="overline grey--text mb-4 font-weight-bold">KONTAKT</div>
                    <div class="d-flex align-center mb-3">
                      <v-icon small color="primary" class="mr-3">mdi-email</v-icon>
                      <span class="body-1 white--text">{{ tacMember.email }}</span>
                    </div>
                    <div class="d-flex align-center mb-3">
                      <v-icon small color="primary" class="mr-3">mdi-phone</v-icon>
                      <span class="body-1 white--text">{{ tacMember.phone }}</span>
                    </div>
                    <div class="d-flex align-start">
                      <v-icon small color="primary" class="mr-3 mt-1">mdi-map-marker</v-icon>
                      <span class="body-1 white--text">{{ tacMember.address }}, {{ tacMember.zipCity }}</span>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item>
              <div v-for="(contract, i) in tacMember.contracts" :key="i" class="mb-4">
                <v-card flat color="#161616" class="rounded-xl border-subtle overflow-hidden">
                  <div class="pa-4 d-flex align-center" :style="`border-left: 4px solid ${contract.color}`">
                    <div>
                      <div class="font-weight-bold white--text subtitle-1">{{ contract.name }}</div>
                      <div class="caption grey--text">{{ contract.start }} - {{ contract.end }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-chip x-small :color="contract.color" label class="font-weight-bold white--text">{{ contract.statusText }}</v-chip>
                  </div>
                  <div v-if="contract.subContracts.length" class="px-4 pb-4">
                    <v-divider class="grey darken-4 mb-3"></v-divider>
                    <div v-for="(sub, k) in contract.subContracts" :key="k" class="d-flex align-center mb-1">
                      <v-icon x-small color="grey" class="mr-2">mdi-plus-circle-outline</v-icon>
                      <span class="caption white--text">{{ sub.name }}</span>
                    </div>
                  </div>
                </v-card>
              </div>
            </v-tab-item>

            <v-tab-item>
              <v-card
                v-for="(comment, index) in tacMember.comments"
                :key="index"
                color="rgba(255, 193, 7, 0.1)"
                class="mb-3 rounded-lg border-subtle"
                flat>
                <v-card-text class="white--text body-2">
                  <div class="caption amber--text font-weight-bold mb-1">TYP: {{ comment.type }}</div>
                  <div style="white-space: pre-wrap">{{ comment.text }}</div>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showMagicDialog" max-width="450">
      <v-card color="#121212" dark class="rounded-xl border-glow">
        <div class="pa-5">
          <div class="d-flex align-center mb-4">
            <v-icon color="purple accent-2" class="mr-2">mdi-auto-fix</v-icon>
            <span class="text-h6 font-weight-bold white--text">AI Anweisung</span>
          </div>

          <p class="body-2 grey--text text--lighten-1 mb-4">Ordne den Absender einem TAC-Profil zu oder gib Roody neue Instruktionen.</p>

          <v-text-field
            v-model="manualEmailInput"
            label="TAC E-Mail suchen"
            prepend-inner-icon="mdi-magnify"
            @click:append="lookupTacUser"
            outlined
            rounded
            dense
            dark
            background-color="#1A1A1A"
            class="mb-2"></v-text-field>

          <div
            v-if="foundTacUser"
            class="mb-4 pa-3 rounded-lg d-flex align-center"
            style="background: rgba(76, 175, 80, 0.1); border: 1px solid rgba(76, 175, 80, 0.3)">
            <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
            <div>
              <div class="font-weight-bold white--text">{{ foundTacUser.fullName }}</div>
              <div class="caption grey--text">Gefunden</div>
            </div>
          </div>

          <v-textarea
            v-model="aiInstructions"
            label="Prompt Instructions"
            placeholder="z.B. 'Sei etwas formeller', 'Du-Form'"
            outlined
            rounded
            rows="3"
            dark
            background-color="#1A1A1A"></v-textarea>

          <div class="d-flex justify-end mt-2">
            <v-btn text class="grey--text mr-2" @click="showMagicDialog = false">Abbrechen</v-btn>
            <v-btn class="gradient-btn rounded-lg" depressed @click="triggerRegenerate" :loading="isRegenerating">
              <v-icon left small>mdi-refresh</v-icon> Generieren
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import socketService from "@/socketService"
import moment from "moment"

export default {
  name: "RoodyEmailNextGen",
  data: () => ({
    // Navigation
    drawer: false,
    folders: [{ name: "INBOX" }, { name: "Sent" }, { name: "Trash" }],
    selectedFolder: "INBOX",

    // Data
    mails: [],
    loadingMails: false,
    activeMail: null,
    search: "",

    // UI State
    showMobileDetail: false,

    // Status Flags
    isSyncingActive: false,
    isRegenerating: false,

    // Popup Info
    showCustomerInfo: false,
    memberTab: 0,

    // Popup Magic/AI
    showMagicDialog: false,
    manualEmailInput: "",
    foundTacUser: null,
    aiInstructions: "",

    // Reply / Forward
    replyMode: false,
    isForwarding: false,
    replyText: "",
    forwardTo: "",
    sending: false,
  }),
  computed: {
    tacMember() {
      const data = this.activeMail?.customerData
      if (data && data.mapped) return data
      return null
    },
    filteredMails() {
      if (!this.search) return this.mails
      const lower = this.search.toLowerCase()
      return this.mails.filter(
        (m) => m.subject?.toLowerCase().includes(lower) || m.from.name?.toLowerCase().includes(lower) || m.from.address?.toLowerCase().includes(lower)
      )
    },
  },
  watch: {
    "$vuetify.breakpoint.mdAndUp"(val) {
      if (val) this.showMobileDetail = false
    },
  },
  async created() {
    socketService.off("ai_mail:sync_start", this.handleSyncStart)
    socketService.off("ai_mail:new_mail", this.handleNewMail)
    socketService.off("ai_mail:update_mail", this.handleUpdateMail)
    socketService.off("ai_mail:sync_complete", this.handleSyncComplete)
  },
  async mounted() {
    if (this.$vuetify.breakpoint.smAndDown) {
      this.drawer = false
    }
    this.checkSyncStatus()
    socketService.on("ai_mail:sync_start", this.handleSyncStart)
    socketService.on("ai_mail:new_mail", this.handleNewMail)
    socketService.on("ai_mail:update_mail", this.handleUpdateMail)
    socketService.on("ai_mail:sync_complete", this.handleSyncComplete)
    await this.fetchFolders()
    await this.loadMails("INBOX")
  },
  beforeDestroy() {
    socketService.off("ai_mail:sync_start", this.handleSyncStart)
    socketService.off("ai_mail:new_mail", this.handleNewMail)
    socketService.off("ai_mail:update_mail", this.handleUpdateMail)
    socketService.off("ai_mail:sync_complete", this.handleSyncComplete)
  },
  methods: {
    // --- SOCKET HANDLER METHODS ---
    handleSyncStart() {
      this.isSyncingActive = true
    },
    handleNewMail(newMail) {
      if (newMail.folder === this.selectedFolder) {
        const exists = this.mails.find((m) => m._id === newMail._id)
        if (!exists) this.mails.unshift(newMail)
      }
    },
    handleUpdateMail(updatedMail) {
      const index = this.mails.findIndex((m) => m._id === updatedMail._id)
      if (index !== -1) {
        this.$set(this.mails, index, updatedMail)
        if (this.activeMail && this.activeMail._id === updatedMail._id) {
          this.activeMail = updatedMail
          if (this.isRegenerating) {
            this.isRegenerating = false
            this.showMagicDialog = false
            this.$toast.success("AI Update erhalten")
          }
        }
      }
    },
    handleSyncComplete() {
      this.isSyncingActive = false
      this.$toast.success("Synchronisation fertig")
    },

    // --- METHODS ---
    formatDate(date) {
      const m = moment(date)
      if (m.isSame(moment(), "day")) return m.format("HH:mm")
      return m.format("DD.MM.")
    },
    formatFolderName(name) {
      return name.replace("INBOX.", "")
    },
    getFolderIcon(name) {
      const n = name.toLowerCase()
      if (n.includes("inbox")) return "mdi-inbox-arrow-down"
      if (n.includes("sent")) return "mdi-send-outline"
      if (n.includes("trash")) return "mdi-delete-outline"
      return "mdi-folder-outline"
    },
    getCategoryColor(cat) {
      if (!cat) return "grey darken-3"
      const c = cat.toLowerCase()
      if (c.includes("cancel") || c.includes("künd")) return "red darken-2"
      if (c.includes("sales") || c.includes("verkau")) return "green darken-2"
      if (c.includes("support")) return "blue darken-2"
      return "grey darken-2"
    },

    async checkSyncStatus() {
      try {
        const status = await api.mail.getStatus()
        if (status && status.isSyncing) this.isSyncingActive = true
      } catch (e) {
        console.error(e)
      }
    },
    onDragStart(evt, mail) {
      evt.dataTransfer.setData("mailId", mail._id)
    },
    async onDrop(evt, targetFolder) {
      const mailId = evt.dataTransfer.getData("mailId")
      if (!mailId) return
      const mailIndex = this.mails.findIndex((m) => m._id === mailId)
      if (mailIndex > -1) this.mails.splice(mailIndex, 1)
      try {
        await api.mail.update(mailId, { folder: targetFolder })
        this.$toast.success(`Verschoben nach ${targetFolder}`)
      } catch (e) {
        this.$toast.error("Fehler beim Verschieben")
        await this.loadMails(this.selectedFolder)
      }
    },
    async fetchFolders() {
      try {
        const res = await api.mail.getFolders()
        if (Array.isArray(res)) this.folders = res
      } catch (e) {
        console.error(e)
      }
    },
    async loadMails(folder) {
      this.loadingMails = true
      this.selectedFolder = folder
      if (this.$vuetify.breakpoint.smAndDown) {
        this.activeMail = null
        this.showMobileDetail = false
      }
      this.replyMode = false
      try {
        const res = await api.mail.getMails({ folder })
        this.mails = res.mails || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingMails = false
      }
    },
    async triggerSync() {
      this.isSyncingActive = true
      try {
        await api.mail.sync()
      } catch (e) {
        if (e.response && e.response.status === 409) this.$toast.info("Sync läuft bereits")
        else {
          this.isSyncingActive = false
          this.$toast.error("Fehler beim Sync")
        }
      }
    },
    async selectMail(mail) {
      if (mail.category === "Processing...") return
      this.activeMail = mail
      this.replyMode = false
      this.showCustomerInfo = false
      if (this.$vuetify.breakpoint.smAndDown) {
        this.showMobileDetail = true
      }
      if (!mail.isRead) {
        mail.isRead = true
        api.mail.update(mail._id, { isRead: true }).catch((e) => console.error(e))
      }
    },
    async archiveMail(mail) {
      try {
        await api.mail.update(mail._id, { folder: "Archive" })
        this.mails = this.mails.filter((m) => m._id !== mail._id)
        this.activeMail = null
        this.showMobileDetail = false
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteMail(mail) {
      try {
        await api.mail.update(mail._id, { folder: "Trash" })
        this.mails = this.mails.filter((m) => m._id !== mail._id)
        this.activeMail = null
        this.showMobileDetail = false
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async lookupTacUser() {
      if (!this.manualEmailInput) return
      try {
        const res = await api.mail.manualTacLookup(this.manualEmailInput)
        if (res && res.mapped) {
          this.foundTacUser = res
          this.$toast.success("Mitglied gefunden!")
        } else {
          this.$toast.info("Nichts gefunden")
          this.foundTacUser = null
        }
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async triggerRegenerate() {
      if (!this.activeMail) return
      this.isRegenerating = true
      this.showMagicDialog = false
      try {
        await api.mail.regenerate({
          mailId: this.activeMail._id,
          newCustomerEmail: this.foundTacUser ? this.manualEmailInput : null,
          instructions: this.aiInstructions,
        })
        this.foundTacUser = null
        this.aiInstructions = ""
        this.manualEmailInput = ""
        this.$toast.info("AI arbeitet...")
      } catch (e) {
        this.isRegenerating = false
        this.$toast.error("Fehler")
      }
    },
    initReply() {
      this.replyMode = true
      this.isForwarding = false
      if (this.activeMail.draft) {
        this.replyText = this.activeMail.draft
      } else {
        this.replyText = `Hallo ${this.activeMail.from.name},\n\n`
      }
    },
    openForward() {
      this.replyMode = true
      this.isForwarding = true
      this.forwardTo = ""
      this.replyText = `
---------- Forwarded message ---------
From: ${this.activeMail.from.name} <${this.activeMail.from.address}>
Date: ${new Date(this.activeMail.date).toLocaleString()}
Subject: ${this.activeMail.subject}

${this.activeMail.textBody || "Inhalt nicht verfügbar"}
      `
    },
    async sendReply() {
      this.sending = true
      try {
        const payload = {
          html: this.replyText.replace(/\n/g, "<br>"),
          replyToId: this.activeMail._id,
        }
        if (this.isForwarding) {
          payload.to = this.forwardTo
          payload.subject = "Fwd: " + this.activeMail.subject
        } else {
          payload.to = this.activeMail.from.address
          payload.subject = "Re: " + this.activeMail.subject
        }
        await api.mail.send(payload)
        this.$toast.success("Gesendet")
        this.replyMode = false
      } catch (e) {
        this.$toast.error("Fehler beim Senden")
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
/* =========================================
   ROODY NEXT GEN STYLES (Deep Dark)
   ========================================= */

@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap");

.font-sans {
  font-family: "Inter", sans-serif !important;
}
.font-mono {
  font-family: "JetBrains Mono", monospace;
}
.tracking-tight {
  letter-spacing: -0.02em;
}
.tracking-wide {
  letter-spacing: 0.05em;
}

/* Backgrounds */
.app-bg {
  background-color: #050505;
}
.bg-pattern {
  background-color: #050505;
  background-image: radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.05) 1px, transparent 0);
  background-size: 40px 40px;
}
.bg-gradient-subtle {
  background: linear-gradient(180deg, rgba(18, 18, 18, 1) 0%, rgba(5, 5, 5, 1) 100%);
}

/* Glassmorphism */
.glass-header {
  background: rgba(18, 18, 18, 0.75);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}
.glass-panel {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-panel-dark {
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.glass-pill {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Layout Columns */
.sidebar-panel {
  width: 260px;
  background-color: #000000;
  flex-shrink: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.04);
}
.list-panel {
  width: 380px;
  background-color: #0a0a0a;
  flex-shrink: 0;
}
@media (max-width: 959px) {
  .list-panel {
    width: 100%;
  }
}
.detail-panel {
  background-color: #050505;
  position: relative;
  overflow: hidden;
}
.mobile-fullscreen {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
  background-color: #050505;
}

/* Gradients & Glows */
.gradient-btn {
  background: linear-gradient(135deg, #7c4dff 0%, #448aff 100%);
  box-shadow: 0 4px 15px rgba(124, 77, 255, 0.3);
  transition: all 0.3s ease;
}
.gradient-btn:hover {
  box-shadow: 0 6px 20px rgba(124, 77, 255, 0.5);
  transform: translateY(-1px);
}
.gradient-header {
  background: linear-gradient(to right bottom, #212121, #000000);
}
.brand-glow {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #7c4dff, #00e5ff);
  box-shadow: 0 0 15px rgba(124, 77, 255, 0.4);
}
.active-folder-desktop {
  background: rgba(255, 255, 255, 0.06);
  border-right: 3px solid #7c4dff;
}

/* Mail Cards */
.mail-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid transparent;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.mail-card:hover {
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}
.active-mail-card {
  background: rgba(124, 77, 255, 0.08) !important;
  border: 1px solid rgba(124, 77, 255, 0.3) !important;
  box-shadow: 0 0 20px rgba(124, 77, 255, 0.05);
}
.unread-mail {
  border-left: 3px solid #00e5ff;
}

/* Mail Render */
.mail-renderer {
  background-color: #ffffff;
  color: #111;
  /* Smart Invert for dark mode logic */
  filter: invert(0.92) hue-rotate(180deg);
}
.mail-renderer.strip-images >>> img {
  display: none !important;
}
.mail-renderer >>> a {
  color: #2962ff;
  font-weight: bold;
}

/* AI Elements */
.ai-analysis-box {
  background: linear-gradient(145deg, rgba(124, 77, 255, 0.05) 0%, rgba(0, 0, 0, 0) 100%);
  border: 1px solid rgba(124, 77, 255, 0.2);
}
.ai-draft-card {
  border: 1px solid rgba(124, 77, 255, 0.2);
  background: #0f0f0f;
}
.draft-header {
  background: linear-gradient(90deg, #7c4dff 0%, #448aff 100%);
}
.ai-loader {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(124, 77, 255, 0.3);
  border-radius: 50%;
  border-top-color: #7c4dff;
  animation: spin 1s ease-in-out infinite;
}

/* Inputs */
.modern-search-field >>> .v-input__slot {
  min-height: 40px !important;
}
.modern-input {
  background: transparent;
  border: none;
  color: white;
  outline: none;
  font-size: 14px;
}
.no-border-input >>> .v-input__slot::before,
.no-border-input >>> .v-input__slot::after {
  display: none !important;
}

/* Scrollbars */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

/* Utilities */
.border-subtle {
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.border-b-subtle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.border-t-subtle {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.border-r-subtle {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}
.border-glow {
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}
.z-index-10 {
  z-index: 10;
  position: sticky;
  top: 0;
}
.pointer {
  cursor: pointer;
}
.shadow-lg {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

/* Animations */
.fade-in-out {
  animation: fade 2s infinite;
}
@keyframes fade {
  0%,
  100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
